import { useQuery } from '@tanstack/react-query';
import { parse, stringify } from 'query-string';
import React from 'react';
import { useHistory } from 'react-router-dom';

import Button from '@dashboard/components/Button';
import CreateWithAIButton from '@dashboard/components/CreateWithAIButton';
import PageHeader from '@dashboard/components/PageHeader';
import PagesList from '@dashboard/components/PagesList';
import { ToggleGroup, ToggleGroupItem } from '@dashboard/components/ui/toggle-group';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

const emptyState = {
  searchTitle: 'No events found',
  filteredTitle: 'No events found',
  defaultTitle: 'Create your first event',
  defaultSubtitle: 'Sell tickets and check in guests.',
  createLabel: 'Create event',
};

const EVENT_FILTERS = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Past' },
  { value: 'all', label: 'All' },
];

const DEFAULT_EVENT_FILTER = 'upcoming';

function getFilterFromQuery(value) {
  return EVENT_FILTERS.some(f => f.value === value) ? value : DEFAULT_EVENT_FILTER;
}

const Events = () => {
  const query = useUrlQuery();
  const history = useHistory();
  const { sellerId, token } = useAuth();
  const { getPages } = usePageQueries();

  const shouldProbe = !!sellerId && !!token && !query.type;

  const { data: upcomingProbe } = useQuery({
    queryKey: ['pages', sellerId, 'events-upcoming-probe'],
    queryFn: () =>
      getPages(sellerId, {
        type: ['event'],
        status: 'published',
        eventScope: 'upcoming',
        limit: 1,
      }),
    enabled: shouldProbe,
    staleTime: 0,
  });

  const probeSaysNoUpcoming = !!upcomingProbe && upcomingProbe.data?.length === 0;

  const eventFilter = query.type
    ? getFilterFromQuery(query.type)
    : probeSaysNoUpcoming
      ? 'all'
      : DEFAULT_EVENT_FILTER;

  const updateFilter = value => {
    const current = parse(history.location.search);
    history.replace({
      search: stringify({ ...current, type: value || DEFAULT_EVENT_FILTER }),
    });
  };

  return (
    <>
      <div className="p-4 space-y-4">
        <PageHeader
          title="Events"
          topBarActions={
            <div className="flex items-center gap-2">
              <CreateWithAIButton pageType="event" />

              <Button to="/events/new" appearance="primary">
                Create event
              </Button>
            </div>
          }
        />

        <PagesList
          type="event"
          emptyState={emptyState}
          createHref="/events/new"
          searchPlaceholder="Search events"
          extraFilter={{ eventScope: eventFilter }}
          leftControls={
            <ToggleGroup
              type="single"
              variant="outline"
              value={eventFilter}
              onValueChange={value => {
                if (value) updateFilter(value);
              }}
              className="rounded-full"
            >
              {EVENT_FILTERS.map(option => (
                <ToggleGroupItem
                  key={option.value}
                  value={option.value}
                  className="text-sm first:rounded-l-full last:rounded-r-full"
                >
                  {option.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          }
        />
      </div>
    </>
  );
};

export default Events;
